import React, { useState, useMemo } from 'react';
import { Table, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './LoanDataDisplay.scss';

interface AverageRateItem {
  loanNumber: string;
  customerName: string;
  customerPhone: string;
  metal: string;
  startDate: string | null;
  lastPaymentDate: string | null;
  originalPrincipal: number;
  pendingPrincipal: number;
  pendingInterest: number;
  netWeight: number;
  todayGoldRate: number;
  liveLoanAverage: number;
  highlight: 'green' | 'red';
}

interface AverageRateTrackerProps {
  data: AverageRateItem[];
}

const fmt = (val: number) => `₹${val.toLocaleString('en-IN')}`;

const formatDate = (val: string | null) => {
  if (val === null || val === undefined) return '-';
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const columns: ColumnsType<AverageRateItem & { _sno: number }> = [
  {
    title: 'S.No',
    dataIndex: '_sno',
    key: '_sno',
    width: 60,
  },
  {
    title: 'Loan Number',
    dataIndex: 'loanNumber',
    key: 'loanNumber',
    render: (val: string) => <strong>{val}</strong>,
  },
  {
    title: 'Customer Name',
    dataIndex: 'customerName',
    key: 'customerName',
  },
  {
    title: 'Phone',
    dataIndex: 'customerPhone',
    key: 'customerPhone',
  },
  {
    title: 'Loan Start Date',
    dataIndex: 'startDate',
    key: 'startDate',
    render: formatDate,
  },
  {
    title: 'Last Payment Date',
    dataIndex: 'lastPaymentDate',
    key: 'lastPaymentDate',
    render: formatDate,
  },
  {
    title: 'Original Principal (₹)',
    dataIndex: 'originalPrincipal',
    key: 'originalPrincipal',
    render: fmt,
  },
  {
    title: 'Pending Principal (₹)',
    dataIndex: 'pendingPrincipal',
    key: 'pendingPrincipal',
    render: fmt,
  },
  {
    title: 'Pending Interest (₹)',
    dataIndex: 'pendingInterest',
    key: 'pendingInterest',
    render: fmt,
  },
  {
    title: 'Metal',
    dataIndex: 'metal',
    key: 'metal',
  },
  {
    title: 'Net Weight (g)',
    dataIndex: 'netWeight',
    key: 'netWeight',
    render: (val: number) => `${val.toFixed(3)} g`,
  },
  {
    title: "Today's Gold Rate (₹/g)",
    dataIndex: 'todayGoldRate',
    key: 'todayGoldRate',
    render: fmt,
  },
  {
    title: 'Live Loan Average (₹/g)',
    dataIndex: 'liveLoanAverage',
    key: 'liveLoanAverage',
    render: (val: number, record) => (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: 4,
          background: record.highlight === 'green' ? '#d4edda' : '#f8d7da',
          color: record.highlight === 'green' ? '#155724' : '#721c24',
          fontWeight: 600,
        }}
      >
        {fmt(val)}
      </span>
    ),
  },
];

const AverageRateTrackerDisplay: React.FC<AverageRateTrackerProps> = ({ data }) => {
  const [metalFilter, setMetalFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const metalOptions = useMemo(() => {
    const unique = Array.from(new Set(data.map((d) => d.metal).filter(Boolean)));
    return [{ label: 'All Metals', value: 'all' }, ...unique.map((m) => ({ label: m, value: m }))];
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((d) => {
      const metalMatch = metalFilter === 'all' || d.metal === metalFilter;
      const statusMatch =
        statusFilter === 'all' ||
        (statusFilter === 'below' && d.highlight === 'green') ||
        (statusFilter === 'above' && d.highlight === 'red');
      return metalMatch && statusMatch;
    });
  }, [data, metalFilter, statusFilter]);

  const dataWithSno = filtered.map((item, idx) => ({ ...item, _sno: idx + 1 }));
  const belowAverage = filtered.filter((d) => d.highlight === 'green').length;
  const aboveAverage = filtered.filter((d) => d.highlight === 'red').length;

  return (
    <div className="loan-data-container">
      <h2>Average Rate Tracker</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div
          style={{
            padding: '10px 24px',
            borderRadius: 6,
            background: '#d4edda',
            color: '#155724',
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          Below Average: {belowAverage}
        </div>
        <div
          style={{
            padding: '10px 24px',
            borderRadius: 6,
            background: '#f8d7da',
            color: '#721c24',
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          Above Average: {aboveAverage}
        </div>
        <Select
          style={{ width: 180 }}
          value={metalFilter}
          onChange={setMetalFilter}
          options={metalOptions}
          placeholder="Filter by Metal"
        />
        <Select
          style={{ width: 180 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { label: 'All Status', value: 'all' },
            { label: 'Below Average', value: 'below' },
            { label: 'Above Average', value: 'above' },
          ]}
          placeholder="Filter by Status"
        />
      </div>
      <Table
        columns={columns}
        dataSource={dataWithSno}
        rowKey="_sno"
        pagination={{ pageSize: 20, showTotal: (t) => `${t} records` }}
        scroll={{ x: true }}
        bordered
      />
    </div>
  );
};

export default AverageRateTrackerDisplay;
